/**
 * API Types - Generated from TusCourses API (swagger.json)
 *
 * These types match the backend DTOs exactly
 */

// =============================================================================
// Auth Types
// =============================================================================

export interface LoginRequest {
  username: string
  password: string
  rememberMe?: boolean
  assumeRole?: string
}

/**
 * Antwort auf `POST /Auth/login`.
 *
 * `jwtToken` und `sessionId` stehen hier ABSICHTLICH nicht mehr, obwohl das Backend sie weiterhin
 * im Rumpf mitschickt: die Sitzung läuft über ein httpOnly-Cookie, das JavaScript nicht lesen
 * kann. Was hier nicht steht, kann auch nicht versehentlich in den localStorage wandern — bis zum
 * 2026-09-02 lag das JWT genau dort, und jede XSS hätte es mitnehmen können.
 */
export interface LoginResponse {
  trainerName: string
  mustChangePassword: boolean
  role: string
  userId: number
  personId: number | null
}

/**
 * Antwort auf `GET /Auth/me` — stellt den Anmeldezustand aus dem Cookie wieder her.
 * Bei fehlender oder abgelaufener Sitzung antwortet der Endpunkt mit 401.
 */
export interface CurrentUserResponse {
  authenticated: boolean
  userId: number
  personId: number | null
  username: string
  email: string | null
  fullName: string
  role: string
  mustChangePassword: boolean
  avatarUrl: string | null
}

// =============================================================================
// Course Types
// =============================================================================

export interface CourseDto {
  id: number
  createdAt: string
  updatedAt: string
  title: string
  number: number
  instructors: CourseInstructorDto[]
  participantCount: number
  sportId?: number
  sportName?: string
  sportColor?: string
  sportIcon?: string
  sportIconSvg?: string
}

export interface CourseInstructorDto {
  id: number
  createdAt: string
  updatedAt: string
  courseId: number
  memberId: number
  instructorName: string
  isLeadTrainer: boolean
}

/** Rohe API-Antwort von GET /api/Instructors */
export interface InstructorApiDto {
  id: number
  firstName: string
  lastName: string
  fullName: string
}

/** Flache Trainer-Zusammenfassung (nach Mapping im Store) */
export interface InstructorSummaryDto {
  id: number // = memberId
  name: string // Vollname, getrimmt
}

export interface CreateCourseDto {
  title: string
  number: number
  sportId?: number
  instructors?: CreateCourseInstructorDto[]
  schedules: CreateCourseScheduleDto[]
}

export interface CreateCourseInstructorDto {
  courseId: number
  memberId: number
  isLeadTrainer: boolean
}

export interface CreateCourseScheduleDto {
  dayOfWeek: number
  startTime: string
  endTime: string
  locationId?: number
}

export interface UpdateCourseDto {
  title?: string
  number?: number
  sportId?: number
}

// =============================================================================
// Course Schedule Types
// =============================================================================

export interface CourseScheduleDto {
  id: number
  createdAt: string
  updatedAt: string
  courseId: number
  dayOfWeek: number // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  startTime: string // ISO DateTime (time portion relevant)
  duration: string // TimeSpan format "HH:mm:ss"
  location?: string
  isActive: boolean
  startDate?: string // ISO Date - schedule valid from
  endDate?: string | null // ISO Date - schedule valid until (null = forever)
  exceptionalDates?: string[] // Dates to skip
}

// =============================================================================
// Course Session Types
// =============================================================================

export interface CourseSessionDto {
  id: number
  createdAt: string
  updatedAt: string
  courseId: number
  date: string
  startTime?: string // Time "HH:mm:ss"
  endTime?: string // Time "HH:mm:ss"
  location?: string
  isCancelled?: boolean
  isException?: boolean
  leadInstructorId?: number
  coInstructorId?: number
  leadInstructorName?: string
  coInstructorName?: string
  isBilled?: boolean
}

export interface CreateCourseSessionDto {
  courseId: number
  date: string // ISO Date "YYYY-MM-DD"
  startTime?: string // Time "HH:mm:ss"
  endTime?: string // Time "HH:mm:ss"
  location?: string
  isCancelled?: boolean
  leadInstructorId?: number
  coInstructorId?: number
}

export interface UpdateCourseSessionDto {
  date?: string
  leadInstructorId?: number
  coInstructorId?: number
}

// =============================================================================
// Attendance Types
// =============================================================================

/**
 * Anwesenheit — Datenmodell „Existenz = Anwesenheit".
 *
 *   Datensatz existiert        → die Person war da
 *   Datensatz existiert nicht  → die Person war nicht da
 *
 * Es gibt bewusst **kein** `isPresent`: das Backend hat das Feld am 2026-08-04 aus allen
 * Attendance-DTOs entfernt und `PUT /api/Attendances/{id}` gelöscht („abwesend" = DELETE).
 * Wer hier ein Flag ergänzt, baut wieder den stillen No-Op ein, der vier Monate lang eine
 * Fehldiagnose getragen hat — siehe docs/BUGFIX_ATTENDANCE_ABSENT.md.
 *
 * Nur `LocalAttendance` (IndexedDB, src/types/local.types.ts) trägt ein `isPresent`: dort ist es
 * die noch nicht synchronisierte Absicht des Nutzers, kein Serverzustand.
 */
export interface AttendanceDto {
  id: number
  createdAt: string
  updatedAt: string
  personId: number
  participantName: string
  courseSessionId: number
  /**
   * Ausgetretenes Mitglied (historisch oder gelöscht). Zum Zeitpunkt eines Termins bis zum
   * Austritt war es ein normales Mitglied und erscheint als solches; neue Anwesenheiten für
   * spätere Termine lehnt das Backend mit 409 ab. Optional für ältere Backend-Stände.
   */
  isFormerMember?: boolean
  membershipEndedOn?: string | null
}

export interface CreateAttendanceDto {
  personId: number
  courseSessionId: number
}

// =============================================================================
// Person Types (for guest creation)
// =============================================================================

export interface GuardianSummaryDto {
  guardianPersonId: number
  guardianName: string
  relationship?: string
  email?: string
  phoneNumber?: string
}

export interface PersonDto {
  id: number
  firstName: string
  lastName: string
  fullName: string
  dateOfBirth: string
  gender: Gender
  email?: string
  phoneNumber?: string
  address?: string
  city?: string
  state?: string
  zipCode?: string
  isMember: boolean
  // Anmeldedaten
  datumAnmeldung?: string
  datumKlk?: string
  vereinskooperation?: string
  // Demografie
  nationality?: string
  birthplace?: string
  // Zustimmungen
  consentWhatsApp?: boolean
  consentDataUsage?: boolean
  consentPhotoVideo?: boolean
  consentFeeRegulations?: boolean
  consentStatutes?: boolean
  consentPrivacy?: boolean
  // WhatsApp-Notfallkontakte
  whatsAppContact1Name?: string
  whatsAppContact1Phone?: string
  whatsAppContact2Name?: string
  whatsAppContact2Phone?: string
  // Erziehungsberechtigte
  guardians?: GuardianSummaryDto[]
  // Foto
  hasPhoto?: boolean
  photoUrl?: string | null
  thumbnailUrl?: string | null
}

export interface CreatePersonDto {
  firstName: string
  lastName: string
  dateOfBirth: string
  gender: Gender
  email: string
  phoneNumber?: string
}

// =============================================================================
// Participant Types
// =============================================================================

export interface ParticipantDto {
  id: number
  createdAt: string
  updatedAt: string
  firstName: string
  lastName: string
  externalId?: string
  memberNumber: number
  dateOfBirth: string
  email?: string
  phoneNumber?: string
  address?: string
  city?: string
  state?: string
  zipCode?: string
  gender: Gender
  externalLastModified?: string
  nationality?: string
  birthplace?: string
  datumAnmeldung?: string
  datumKlk?: string
  consentPhotoVideo?: boolean
  guardians?: GuardianSummaryDto[]
  hasPhoto?: boolean
}

export enum Gender {
  Unknown = 0,
  Male = 1,
  Female = 2,
  Diverse = 3,
}

export interface CreateParticipantDto {
  firstName: string
  lastName: string
  memberNumber: number
  dateOfBirth: string
  email?: string
  phoneNumber?: string
  gender?: Gender
}

export interface UpdateParticipantDto {
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  address?: string
  city?: string
  state?: string
  zipCode?: string
  gender?: Gender
}

// =============================================================================
// Course Registration Types
// =============================================================================

/**
 * Raw API response for course registrations
 * (as returned by /api/CourseRegistrations/by-course/{id})
 */
export interface CourseRegistrationApiResponse {
  id: number
  createdAt: string
  updatedAt: string
  courseId: number
  personId: number
  isProvisional: boolean
  course?: {
    title: string
    number: number
    id: number
  }
  person: {
    firstName: string
    lastName: string
    fullName: string
    dateOfBirth?: string
    email?: string
    phoneNumber?: string
    id: number
    /** Ausgetretenes Mitglied — nur bei `by-course?includeFormer=true` geliefert */
    isHistorical?: boolean
    membershipEndedOn?: string | null
  }
}

/**
 * Normalized registration DTO for internal use
 */
export interface CourseRegistrationDto {
  id: number
  createdAt: string
  updatedAt: string
  courseId: number
  participantId: number
  participantName: string
  participantFirstName: string
  participantLastName: string
  participantEmail?: string
  participantPhone?: string
  participantBirthDate?: string
  registrationDate: string
  isActive: boolean
  /** true = vom Trainer hinzugefügtes "zusätzliches Mitglied" */
  isProvisional: boolean
  /**
   * Ausgetretenes Mitglied. Maßgeblich ist das Termindatum: in Terminen bis einschließlich
   * `membershipEndedOn` war die Person ein normales Mitglied und erscheint als solches.
   */
  isHistorical?: boolean
  membershipEndedOn?: string | null
}

export interface CreateCourseRegistrationDto {
  courseId: number
  personId: number
  isProvisional?: boolean
}

export interface UpdateCourseRegistrationDto {
  courseId: number
  personId: number
  isProvisional: boolean
}

// =============================================================================
// Instructor Accounting Types (formerly QuarterlyAccounting)
// =============================================================================

export enum BillingPeriodType {
  Quarterly = 0,
  Monthly = 1,
}

export interface InstructorAccountSummaryDto {
  id: number
  instructorId: number
  instructorName: string
  year: number
  period: number
  billingPeriodType: BillingPeriodType
  status: InstructorAccountStatus
  totalAmount: number
  totalHours: number
  courseCount: number
  submittedAt?: string
  submittedByUserId?: number
  authorizedAt?: string
  approvedAt?: string
  paidOutAt?: string
  canPayout?: boolean
  createdAt: string
  updatedAt: string
  periodDisplayName: string
  statusDisplayName: string
  canEdit: boolean
  canSubmit: boolean
  canAuthorize: boolean
  canApprove: boolean
}

export interface InstructorAccountDto {
  id: number
  createdAt: string
  updatedAt: string
  instructorId: number
  year: number
  period: number
  billingPeriodType: BillingPeriodType
  submittedAt?: string
  submittedByUserId?: number
  authorizedAt?: string
  authorizedByUserId?: number
  approvedAt?: string
  approvedByUserId?: number
  paidOutAt?: string
  paidOutByUserId?: number
  totalAmount: number
  totalLeadHours: number
  totalCoHours: number
  totalHours: number
  courseAccountings: CourseAccountingDto[] | null
  status: InstructorAccountStatus
  periodDisplayName: string
  statusDisplayName: string
  /** Embedded instructor (MemberDto) — contains trainer's current billingPeriodType */
  instructor?: {
    id: number
    firstName: string
    lastName: string
    billingPeriodType: BillingPeriodType
  }
}

export interface CourseAccountingDto {
  id: number
  createdAt: string
  updatedAt: string
  instructorAccountId: number
  courseId: number
  courseName: string | null
  courseNumber: number | null
  totalLeadHours: number
  totalCoHours: number
  totalAmount: number
  leadInstructorRate: number
  coInstructorRate: number
  totalHours: number
  sessionAccountings?: CourseSessionAccountingDto[]
}

export interface CourseSessionAccountingDto {
  courseSessionId: number
  sessionDate: string // ISO datetime "2026-01-07T18:00:00"
  startTime: string | null // "HH:mm" z.B. "18:00"
  endTime: string | null // "HH:mm" z.B. "19:30"
  isLeadInstructor: boolean
  hours: number
  rate: number
  amount: number
  roleDisplayName: string | null
}

export enum InstructorAccountStatus {
  Draft = 0,
  Submitted = 1,
  Authorized = 2,
  Approved = 3,
  PaidOut = 4,
}

export interface CreateInstructorAccountRequest {
  instructorId?: number // optional — API leitet aus JWT ab wenn weggelassen
  year: number
  period: number
  billingPeriodType?: BillingPeriodType
}

// =============================================================================
// User Registration Types
// =============================================================================

/**
 * Client source identifier for registration requests (I-112)
 * Determines how the app name appears in notification emails
 */
export type ClientSource = 'trainer-app' | 'kursverwaltung'

export interface ManualRegistrationRequest {
  email: string
  firstName: string
  lastName: string
  username: string
  password: string
  clientSource?: ClientSource
}

export interface RegistrationResponse {
  success: boolean
  message: string
  registrationToken?: string
}

export interface RegistrationStatusResponse {
  status: RegistrationStatus
  statusText: string
  email: string
  firstName: string
  lastName: string
  requestedAt: string
  approvedAt?: string
  approvedRole?: string
  adminComments?: string
}

export enum RegistrationStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
}

// =============================================================================
// API Error Types
// =============================================================================

export interface ApiError {
  type?: string
  title: string
  status: number
  detail?: string
  errors?: Record<string, string[]>
  traceId?: string
  /**
   * Klartextmeldung des Backends.
   *
   * Nicht Teil von RFC 9457 (`problem+json`), aber genau das Feld, das dieser Backend-Controller
   * bei Auth-Fehlern liefert: `{ "message": "InvalidCredentials" }`. Ohne dieses Feld fiel
   * `getErrorMessage` auf `error.message` von Axios zurueck, und der Trainer las
   * „Request failed with status code 401" — am 2026-08-22 so gemessen.
   */
  message?: string
}

// =============================================================================
// Pagination Types
// =============================================================================

export interface PaginatedResult<T> {
  items: T[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

// =============================================================================
// Query Parameter Types
// =============================================================================

export interface CoursesQueryParams {
  Title?: string
  Instructor?: string
  LeadInstructor?: string
  Page?: number
  PageSize?: number
  IncludeDeleted?: boolean
}

export interface InstructorAccountingQueryParams {
  instructorId?: number
  year?: number
  period?: number
}
